<template>
  <div>
    <div v-if="!loading && !isOtherOverheadEmpty" class="mb-3">
      <v-app>
        <div class="d-flex justify-end">
          <erase-icon-button
            v-if="removeButtonVisible"
            class="mr-2"
            @click="onDelete"
          />
          <business-guide-search v-model="searchValue" />
        </div>
      </v-app>
    </div>

    <ib-list-view-placeholder
      v-if="!loading && isOtherOverheadEmpty"
      :disabled="!canEdit || disabled"
      :title="$tc('addOverHead')"
      @click="onShowAddNewDialog"
    />
    <template v-else>
      <el-row
        v-if="!loading"
        :gutter="20"
        class="d-flex flex-wrap"
      >
        <el-col :md="12">
          <add-new-card
            :disabled="!canEdit"
            class="mb-2 py-md-0"
            :title="$tc('addOverHead')"
            horizontal
            min-height="62px"
            @click="onShowAddNewDialog"
          />
        </el-col>
        <el-col
          v-for="otherOverhead in filterOtherOverheads"
          :key="otherOverhead.id"
          class="d-flex mb-2"
          :md="12"
        >
          <div class="d-flex w-100">
            <set-up-and-projections-card
              class="w-100"
              :entity="otherOverhead"
              :name="otherOverhead.name"
              :date="otherOverheadDate(otherOverhead)"
              :amount="otherOverheadAmount(otherOverhead)"
              :frequency="otherOverhead.oneTime ? $t('oneTime') :$t('pages.businessGuide.monthlyAverage')"
              :type="otherOverhead.type"
              view="list"
              :disabled="!canEdit"
              @edit="onEditOtherOverhead"
              @copy="onCopyOtherOverhead"
              @delete="onDeleteOtherOverhead"
            />
          </div>
        </el-col>
      </el-row>
    </template>

    <!-- Dialog Add Other Overhead -->
    <dialog-add-other-overhead
      v-if="canEdit"
      :dialog-add-other-overhead-visible="dialogAddNewVisible"
      :other-overhead-action="otherOverheadAction"
      :options="addOtherOverheadOptions"
      @close-dialog-add-other-overhead="dialogAddNewVisible = false"
      @close-dialog-on-button="dialogAddNewVisible = false; otherOverheadAction = null"
      @on-update-other-overheads-view="onUpdateAddNewDialog()"
    />
    <!-- /Dialog Add Other Overhead -->
  </div>
</template>

<script>
import { mapActions, mapGetters, mapState } from 'vuex'
import AddNewCard from '@/components/_v2/AddNewCard.vue'
import BusinessGuideSearch from '@/views/Home/Components/BusinessGuideSearch.vue'
import DialogAddOtherOverhead from '@/views/Home/StoryMode/Forecast/OtherOverheads/Dialogs/DialogAddOtherOverhead.vue'
import EraseIconButton from '@/components/_v2/EraseIconButton.vue'
import EventBus from '@/event-bus'
import MixinApiCalculations from '@/mixins/apiCalculations'
import MixinCurrencies from '@/mixins/currencies'
import MixinDeleteIdeaExamples from '@/mixins/deleteIdeaExamples'
import MixinIdeaRoles from '@/mixins/permissions/ideaRoles'
import SetUpAndProjectionsCard from '@/views/Home/StoryMode/Components/Cards/SetUpAndProjectionsCard.vue'
import addOtherOverheadOptions from '@/views/Home/StoryMode/Forecast/OtherOverheads/Dialogs/addOtherOverheadOptions'

export default {
  name: 'OtherOverheadsList',

  components: { EraseIconButton, BusinessGuideSearch, DialogAddOtherOverhead, AddNewCard, SetUpAndProjectionsCard },

  mixins: [MixinApiCalculations, MixinCurrencies, MixinIdeaRoles, MixinDeleteIdeaExamples],

  props: {
    disabled: {
      type: Boolean,
      default: false
    }
  },

  data () {
    return {
      searchValue: '',
      loading: false,
      dialogAddNewVisible: false,
      otherOverheadAction: null,
      addOtherOverheadOptions
    }
  },

  computed: {
    ...mapState(['idea']),

    ...mapGetters('idea', {
      otherOverheads: 'getOtherOverheads'
    }),

    isOtherOverheadEmpty () {
      return !this.otherOverheads.length
    },

    filterOtherOverheads () {
      if (this.searchValue !== '' && this.searchValue) {
        return this.otherOverheads.filter(item => item.name.toUpperCase().includes(this.searchValue.toUpperCase()))
      }

      return this.otherOverheads
    },

    removeButtonVisible () {
      return this.canEdit && this.otherOverheadHasItemsFromTemplate
    }
  },

  mounted () {
    EventBus.$on('add-edit', () => {
      this.onShowAddNewDialog()
    })

    EventBus.$on('remove-examples', () => {
      this.deleteExamples()
    })

    EventBus.$on('reload', () => {
      this.onUpdateAddNewDialog()
    })
  },

  beforeDestroy () {
    EventBus.$off('add-edit')
    EventBus.$off('remove-examples')
    EventBus.$off('reload')
  },

  methods: {
    ...mapActions('idea', [
      'deleteStoryModeOtherOverhead'
    ]),

    onEditOtherOverhead (otherOverhead) {
      this.otherOverheadAction = Object.assign({}, otherOverhead)
      this.dialogAddNewVisible = true
    },

    onCopyOtherOverhead (otherOverhead) {
      this.otherOverheadAction = Object.assign({}, otherOverhead)
      this.otherOverheadAction.id = 0
      this.dialogAddNewVisible = true
    },

    onDeleteOtherOverhead (otherOverhead) {
      this.emitLoading(true)
      this.deleteStoryModeOtherOverhead(otherOverhead.id)
        .then(async () => {
          await this.getOtherOverheads()
          await this.getTotalCosts()
          await this.getProfitAndCashFlow()
          this.emitLoading(false)
        })
    },

    onShowAddNewDialog () {
      this.getOtherOverheads()
      this.dialogAddNewVisible = true
    },

    onUpdateAddNewDialog () {
      this.emitLoading(true)
      Promise.all([
        this.getOtherOverheads(),
        this.getTotalCosts(),
        this.getProfitAndCashFlow()
      ])
        .then(() => {
          this.emitLoading(false)
        })
        .finally(() => {
          this.emitLoading(false)
        })
    },

    deleteExamples () {
      this.deleteExamplesMixin('other-overheads')
        .then(_ => { this.onUpdateAddNewDialog() })
    },

    getFormattedDate (date) {
      return this.$moment(date).format('MMM YYYY')
    },

    otherOverheadDate (otherOverhead) {
      if (otherOverhead.oneTime) {
        return this.getFormattedDate(otherOverhead.whenWillItOccur)
      }

      return `${this.getFormattedDate(otherOverhead.starts)} - ${this.getFormattedDate(otherOverhead.ends)}`
    },

    otherOverheadAmount (otherOverhead) {
      if (otherOverhead.average && otherOverhead.oneTime === false) {
        return this.getFormattedAmount(otherOverhead.average)
      }

      return this.getFormattedAmount(otherOverhead.amount)
    },

    emitLoading (value) {
      this.loading = value
      EventBus.$emit('loading', value)
      this.$emit('loading', value)
    }
  }
}
</script>
